import { config } from "../config";
import { getMicrosoftAuthUrl, parseMicrosoftCallback, generateState } from "../config/microsoft";

export class MicrosoftAuthService {
	private static instance: MicrosoftAuthService;
	private readonly STATE_KEY = "microsoft_oauth_state";

	private constructor() {}

	public static getInstance(): MicrosoftAuthService {
		if (!MicrosoftAuthService.instance) {
			MicrosoftAuthService.instance = new MicrosoftAuthService();
		}
		return MicrosoftAuthService.instance;
	}

	/**
	 * Redirect to Microsoft login
	 */
	public async redirectToLogin(): Promise<void> {
		try {
			// Generate and store state for CSRF protection
			const state = generateState();
			sessionStorage.setItem(this.STATE_KEY, state);

			// Get Microsoft authorization URL
			const authUrl = await getMicrosoftAuthUrl(state);

			// Check if the URL is generated successfully
			if (!authUrl) {
				console.error("Failed to get Microsoft authorization URL.");
				throw new Error("Failed to get Microsoft authorization URL");
			}

			// Redirect to Microsoft
			window.location.href = authUrl;
		} catch (error) {
			console.error("Failed to redirect to Microsoft login:", error);
			throw new Error("Failed to initiate Microsoft login");
		}
	}

	/**
	 * Handle callback from Microsoft
	 */
	public handleCallback(): { code?: string; state?: string; error?: string } {
		try {
			const callbackData = parseMicrosoftCallback();

			// Verify state parameter for CSRF protection
			if (callbackData.state) {
				const storedState = sessionStorage.getItem(this.STATE_KEY);
				if (storedState !== callbackData.state) {
					throw new Error("Invalid state parameter - possible CSRF attack");
				}
				// Clean up stored state
				sessionStorage.removeItem(this.STATE_KEY);
			}

			return callbackData;
		} catch (error) {
			console.error("Failed to handle Microsoft callback:", error);
			throw error;
		}
	}

	/**
	 * Check if current URL is a callback from Microsoft
	 */
	public isCallback(): boolean {
		const urlParams = new URLSearchParams(window.location.search);
		return urlParams.has("code") && urlParams.has("state");
	}

	/**
	 * Clear URL parameters after handling callback
	 */
	public clearCallbackParams(): void {
		// Remove query parameters from URL without page reload
		const url = new URL(window.location.href);
		url.search = "";
		window.history.replaceState({}, document.title, url.toString());
	}

	/**
	 * Check if Microsoft SSO is configured
	 */
	public isConfigured(): boolean {
		return config.microsoftClientId !== undefined && config.microsoftTenantId !== undefined && config.microsoftRedirectUri !== undefined;
	}
}

// Export singleton instance
export const microsoftAuth = MicrosoftAuthService.getInstance();
